import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Autocomplete,
    TextField,
    Paper,
    Alert,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import Axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import { getAuthHeader } from '../../utils/authToken';
import Dropdown from '../../components/dropdown/Dropdown';
import { generateYears, semesters } from '../../components/dropdown/DropdownData';
import { getCourseYear, getCourseSemester } from '../../utils/CourseDuration';

const ReportGeneration = () => {
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [reportType, setReportType] = useState('midterm');
    const [includeExamScore, setIncludeExamScore] = useState(false);
    const [examScore, setExamScore] = useState(''); // For course mode
    const [examScores, setExamScores] = useState({}); // For student mode: { courseUuid: score }
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    // View mode toggle
    const [viewMode, setViewMode] = useState('course'); // 'course' | 'student'

    // Student mode state
    const [allStudents, setAllStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentCourses, setStudentCourses] = useState([]);
    const [selectedCourses, setSelectedCourses] = useState([]);

    // Year and semester filtering
    const [maxYear, setMaxYear] = useState(0);
    const [minYear, setMinYear] = useState(0);
    const [year, setYear] = useState('');
    const [semester, setSemester] = useState('');
    const season = {'Spring': 1, 'Summer': 2, 'Fall': 3, 'Winter': 4};

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    // Fetch current semester from API
    const fetchCurrentSemester = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/semesters/current-semester`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setYear(response.data.year);
            setSemester(response.data.semester);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching current semester:', error);
                // Fallback to current year if API fails
                setYear(new Date().getFullYear());
            }
        }
    };

    // Fetch max and min years for dropdown
    const fetchMaxMinYears = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/semesters/max-min-years`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setMaxYear(response.data.max_year);
            setMinYear(response.data.min_year);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching years:', error);
            }
        }
    };

    // Fetch courses list (lightweight)
    const fetchCourses = async () => {
        try {
            setLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/courses/all/list`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setCourses(response.data.all_courses || []);
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching courses:', error);
                setError('Failed to load courses');
                setLoading(false);
            }
        }
    };

    // Fetch all students for search (student mode)
    const fetchAllStudents = async () => {
        try {
            setStudentsLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/students/search-list`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setAllStudents(response.data || []);
            setStudentsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching students:', error);
                setError('Failed to load students');
                setStudentsLoading(false);
            }
        }
    };

    // Fetch student's enrolled courses (student mode)
    const fetchStudentCourses = async (studentUuid) => {
        try {
            setLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/courses`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            // API returns courses directly, not nested - filter out any undefined/null courses
            const courseList = (response.data || []).filter(course => course && course.uuid);
            setStudentCourses(courseList);
            setSelectedCourses([]);
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                setStudentCourses([]);
                setSelectedCourses([]);
                setError('');
                setLoading(false);
            } else {
                console.error('Error fetching student courses:', error);
                setError('Failed to load student courses');
                setLoading(false);
            }
        }
    };

    // Initialize: Fetch year ranges and current semester on mount
    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Reports');
        fetchMaxMinYears();
        fetchCurrentSemester();
    }, []);

    // Fetch courses when year/semester changes
    useEffect(() => {
        if (!year || !semester) {
            setCourses([]);
            setFilteredCourses([]);
            return;
        }

        fetchCourses();
    }, [year, semester]);

    // Filter courses based on year and semester
    useEffect(() => {
        if (courses.length === 0) {
            setFilteredCourses([]);
            return;
        }

        const filtered = courses.filter((course) => {
            // Filter by year
            if (year && !getCourseYear(course).includes(year)) {
                return false;
            }
            // Filter by semester
            if (semester !== 'all' && semester && !getCourseSemester(course, year).includes(season[semester])) {
                return false;
            }
            return true;
        });

        setFilteredCourses(filtered);
    }, [courses, year, semester]);

    // Reset selections when year or semester changes
    useEffect(() => {
        setSelectedCourse(null);
        setSelectedStudents([]);
    }, [year, semester]);

    // Reset filtered courses if course selection doesn't match
    useEffect(() => {
        if (selectedCourse && !filteredCourses.find(c => c.uuid === selectedCourse.uuid)) {
            setSelectedCourse(null);
        }
    }, [filteredCourses]);

    // Fetch students when course is selected (course mode)
    useEffect(() => {
        if (viewMode !== 'course' || !selectedCourse) {
            if (viewMode === 'course') {
                setStudents([]);
                setSelectedStudents([]);
            }
            return;
        }

        setLoading(true);
        Axios.get(
            `${process.env.REACT_APP_URL}/api/v1/courses/${selectedCourse.uuid}/students`,
            {
                headers: { Authorization: getAuthHeader() }
            }
        )
            .then((response) => {
                // Extract student objects from student_courses response
                const studentList = response.data.map(sc => sc.student);
                setStudents(studentList || []);
                setSelectedStudents([]);
                setLoading(false);
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                } else if (error.response?.status === 404) {
                    // No students in this course
                    setStudents([]);
                    setSelectedStudents([]);
                    setError('');
                    setLoading(false);
                } else {
                    console.error('Error fetching students:', error);
                    setError('Failed to load students');
                    setLoading(false);
                }
            });
    }, [selectedCourse, viewMode]);

    // Fetch all students when in student mode
    useEffect(() => {
        if (viewMode !== 'student') {
            return;
        }

        fetchAllStudents();
    }, [viewMode]);

    // Fetch student courses when student is selected (student mode)
    useEffect(() => {
        if (!selectedStudent) {
            setStudentCourses([]);
            setSelectedCourses([]);
            setExamScores({});
            return;
        }

        fetchStudentCourses(selectedStudent.uuid);
    }, [selectedStudent]);

    // Clean up exam scores when courses are deselected in student mode
    useEffect(() => {
        if (viewMode !== 'student') return;

        // Remove exam scores for courses that are no longer selected
        const selectedUuids = new Set(selectedCourses.map(c => c.uuid));
        const newExamScores = {};
        for (const uuid in examScores) {
            if (selectedUuids.has(uuid)) {
                newExamScores[uuid] = examScores[uuid];
            }
        }
        if (Object.keys(newExamScores).length !== Object.keys(examScores).length) {
            setExamScores(newExamScores);
        }
    }, [selectedCourses, viewMode]);

    const handleGenerateReports = async () => {
        setGenerating(true);
        setError('');

        try {
            if (viewMode === 'course') {
                // Course mode: one course, multiple students
                if (!selectedCourse) {
                    setError('Please select a course');
                    setGenerating(false);
                    return;
                }

                if (selectedStudents.length === 0) {
                    setError('Please select at least one student');
                    setGenerating(false);
                    return;
                }

                const response = await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/courses/${selectedCourse.uuid}/reports/generate`,
                    {
                        student_ids: selectedStudents.map((s) => s.id),
                        report_type: reportType,
                        exam_score: examScore || null
                    },
                    {
                        headers: {
                            Authorization: getAuthHeader(),
                            'Content-Type': 'application/json'
                        }
                    }
                );

                // Check if there were any errors
                if (response.data.errors && response.data.errors.length > 0) {
                    const errorMessages = response.data.errors.map(e =>
                        `Student ${e.student_id}: ${e.error}`
                    ).join('\n');
                    setError(`Some reports failed:\n${errorMessages}`);
                    setGenerating(false);

                    // Still navigate if at least one succeeded
                    if (response.data.reports && response.data.reports.length > 0) {
                        setTimeout(() => {
                            navigate(`/admin/reports/progress/${response.data.batch_id}`);
                        }, 3000);
                    }
                } else {
                    // No errors, navigate immediately
                    navigate(`/admin/reports/progress/${response.data.batch_id}`);
                }
            } else {
                // Student mode: one student, multiple courses
                if (!selectedStudent) {
                    setError('Please select a student');
                    setGenerating(false);
                    return;
                }

                if (selectedCourses.length === 0) {
                    setError('Please select at least one course');
                    setGenerating(false);
                    return;
                }

                // Generate reports for each course
                const allBatchIds = [];
                const allErrors = [];

                for (const course of selectedCourses) {
                    try {
                        const response = await Axios.post(
                            `${process.env.REACT_APP_URL}/api/v1/courses/${course.uuid}/reports/generate`,
                            {
                                student_ids: [selectedStudent.id],
                                report_type: reportType,
                                exam_score: examScores[course.uuid] || null
                            },
                            {
                                headers: {
                                    Authorization: getAuthHeader(),
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        if (response.data.batch_id) {
                            allBatchIds.push(response.data.batch_id);
                        }

                        if (response.data.errors && response.data.errors.length > 0) {
                            allErrors.push(...response.data.errors.map(e => ({
                                ...e,
                                course: course.course_title
                            })));
                        }
                    } catch (error) {
                        if (error.response?.status === 401) {
                            handleLogout();
                            return;
                        }
                        allErrors.push({
                            course: course.course_title,
                            error: error.response?.data?.error || 'Failed to generate report'
                        });
                    }
                }

                // Show errors if any
                if (allErrors.length > 0) {
                    const errorMessages = allErrors.map(e =>
                        `${e.course}: ${e.error}`
                    ).join('\n');
                    setError(`Some reports failed:\n${errorMessages}`);
                    setGenerating(false);

                    // Still navigate if at least one succeeded
                    if (allBatchIds.length > 0) {
                        setTimeout(() => {
                            navigate(`/admin/reports/progress/${allBatchIds[0]}`);
                        }, 3000);
                    }
                } else {
                    // No errors, navigate to first batch
                    if (allBatchIds.length > 0) {
                        navigate(`/admin/reports/progress/${allBatchIds[0]}`);
                    }
                }
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setError(
                    error.response?.data?.error || 'Failed to generate reports'
                );
                setGenerating(false);
            }
        }
    };

    const handleSelectAll = () => {
        if (viewMode === 'course') {
            if (selectedStudents.length === students.length) {
                setSelectedStudents([]);
            } else {
                setSelectedStudents(students);
            }
        } else {
            // Student mode: select all courses
            if (selectedCourses.length === studentCourses.length) {
                setSelectedCourses([]);
            } else {
                setSelectedCourses(studentCourses);
            }
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title="Generate Student Reports" subtitle="AI-Powered Report Generation" />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* View Mode Toggle */}
            <Box mb={3}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, newMode) => {
                        if (newMode) {
                            setViewMode(newMode);
                            // Reset selections when switching modes
                            setSelectedCourse(null);
                            setSelectedStudents([]);
                            setSelectedStudent(null);
                            setSelectedCourses([]);
                            setExamScore('');
                            setExamScores({});
                            setIncludeExamScore(false);
                            setError('');
                        }
                    }}
                    size="small"
                >
                    <ToggleButton value="course">By Course</ToggleButton>
                    <ToggleButton value="student">By Student</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper sx={{ p: 3, maxWidth: 900 }}>
                {/* COURSE MODE */}
                {viewMode === 'course' && (
                    <>
                        {/* Step 1: Year, Semester, and Course Selection */}
                        <Box mb={4}>
                            <Typography variant="h6" mb={2}>
                                1. Select Year, Semester, and Course
                            </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
                        <Box>
                            <Dropdown
                                maxWidth={150}
                                maxHeight={3}
                                reactUseState={year}
                                setReactUseState={setYear}
                                inputLabel="Year"
                                data={generateYears(minYear, maxYear)}
                            />
                        </Box>
                        <Box>
                            <Dropdown
                                maxWidth={150}
                                maxHeight={3}
                                reactUseState={semester}
                                setReactUseState={setSemester}
                                inputLabel="Semester"
                                data={semesters}
                            />
                        </Box>
                        <Box sx={{ minWidth: 300, maxWidth: 450, flex: 1 }}>
                            <Autocomplete
                                options={filteredCourses}
                                getOptionLabel={(option) =>
                                    `${option.code || ''} - ${option.course_title}`.trim()
                                }
                                value={selectedCourse}
                                onChange={(_, newValue) => {
                                    setSelectedCourse(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Course" placeholder="Choose a course" />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.uuid || option.id}>
                                        {`${option.code || ''} - ${option.course_title}`.trim()}
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                                size="small"
                                disabled={!year || !semester || filteredCourses.length === 0}
                            />
                            {year && semester && (
                                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                                    {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} available
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Step 2: Student Selection */}
                {selectedCourse && (
                    <Box mb={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">
                                2. Select Students ({selectedStudents.length} selected)
                            </Typography>
                            <Button
                                onClick={handleSelectAll}
                                variant="outlined"
                                size="small"
                            >
                                {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </Box>
                        <Autocomplete
                            multiple
                            options={students}
                            getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name}`.trim()
                            }
                            value={selectedStudents}
                            onChange={(_, newValue) => {
                                setSelectedStudents(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Students"
                                    placeholder="Choose students"
                                    helperText={students.length === 0 ? 'No students in this course' : ''}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {`${option.first_name} ${option.last_name}`.trim()}
                                </li>
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disableCloseOnSelect
                        />
                    </Box>
                )}

                {/* Step 3: Report Type Selection */}
                {selectedStudents.length > 0 && (
                    <Box mb={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                <Typography variant="h6" mb={1}>
                                    3. Select Report Type
                                </Typography>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                            >
                                <FormControlLabel
                                    value="midterm"
                                    control={<Radio />}
                                    label="Midterm Report"
                                />
                                <FormControlLabel
                                    value="final"
                                    control={<Radio />}
                                    label="Final Report"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                {/* Step 4: Include Exam Score */}
                {selectedStudents.length > 0 && (
                    <Box mb={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                <Typography variant="h6" mb={1}>
                                    4. Include {reportType === 'midterm' ? 'Midterm' : 'Final'} Exam Score?
                                </Typography>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={includeExamScore ? 'yes' : 'no'}
                                onChange={(e) => {
                                    const include = e.target.value === 'yes';
                                    setIncludeExamScore(include);
                                    if (!include) {
                                        setExamScore('');
                                    }
                                }}
                            >
                                <FormControlLabel
                                    value="no"
                                    control={<Radio />}
                                    label="No"
                                />
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio />}
                                    label="Yes"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                {/* Step 5: Exam Score Input */}
                {selectedStudents.length > 0 && includeExamScore && (
                    <Box mb={3}>
                        <Typography variant="h6" mb={1}>
                            5. Enter {reportType === 'midterm' ? 'Midterm' : 'Final'} Exam Score
                        </Typography>
                        <TextField
                            label={`${reportType === 'midterm' ? 'Midterm' : 'Final'} Score`}
                            value={examScore}
                            onChange={(e) => setExamScore(e.target.value)}
                            placeholder="e.g., 8/10"
                            fullWidth
                            sx={{ maxWidth: 300 }}
                        />
                    </Box>
                )}

                        {/* Generate Button */}
                        {selectedStudents.length > 0 && (
                            <Box mt={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={handleGenerateReports}
                                    disabled={generating}
                                    fullWidth
                                >
                                    {generating
                                        ? 'Generating...'
                                        : `Generate ${selectedStudents.length} Report${selectedStudents.length > 1 ? 's' : ''}`}
                                </Button>
                                <Typography variant="caption" display="block" mt={1} textAlign="center">
                                    This process may take a few minutes. You'll be redirected to track progress.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {/* STUDENT MODE */}
                {viewMode === 'student' && (
                    <>
                        {/* Step 1: Student Selection */}
                        <Box mb={4}>
                            <Typography variant="h6" mb={2}>
                                1. Select Student
                            </Typography>
                            <Autocomplete
                                options={allStudents}
                                getOptionLabel={(option) => {
                                    const parts = [];
                                    if (option.first_name) parts.push(option.first_name);
                                    if (option.last_name) parts.push(option.last_name);
                                    if (option.korean_name) parts.push(`(${option.korean_name})`);
                                    if (option.consultation_number) parts.push(`- #${option.consultation_number}`);
                                    return parts.join(' ');
                                }}
                                value={selectedStudent}
                                onChange={(_, newValue) => {
                                    setSelectedStudent(newValue);
                                }}
                                loading={studentsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Student"
                                        placeholder="Search by name or consultation number"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.uuid}>
                                        {option.first_name} {option.last_name}
                                        {option.korean_name && ` (${option.korean_name})`}
                                        {option.consultation_number && ` - #${option.consultation_number}`}
                                    </li>
                                )}
                                noOptionsText="No students found"
                                isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
                            />
                        </Box>

                        {/* Step 2: Course Selection */}
                        {selectedStudent && (
                            <Box mb={3}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="h6">
                                        2. Select Courses ({selectedCourses.length} selected)
                                    </Typography>
                                    <Button
                                        onClick={handleSelectAll}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {selectedCourses.length === studentCourses.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Box>
                                <Autocomplete
                                    multiple
                                    options={studentCourses}
                                    getOptionLabel={(option) =>
                                        option ? `${option.code || ''} - ${option.course_title || ''}`.trim() : ''
                                    }
                                    value={selectedCourses}
                                    onChange={(_, newValue) => {
                                        setSelectedCourses(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Courses"
                                            placeholder="Choose courses"
                                            helperText={studentCourses.length === 0 ? 'This student is not enrolled in any courses' : ''}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option?.uuid || Math.random()}>
                                            {option ? `${option.code || ''} - ${option.course_title || ''}`.trim() : ''}
                                        </li>
                                    )}
                                    isOptionEqualToValue={(option, value) => option?.uuid === value?.uuid}
                                    disableCloseOnSelect
                                />
                            </Box>
                        )}

                        {/* Step 3: Report Type Selection */}
                        {selectedCourses.length > 0 && (
                            <Box mb={3}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">
                                        <Typography variant="h6" mb={1}>
                                            3. Select Report Type
                                        </Typography>
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={reportType}
                                        onChange={(e) => {
                                            setReportType(e.target.value);
                                            // Clear exam scores when changing report type
                                            setExamScores({});
                                        }}
                                    >
                                        <FormControlLabel
                                            value="midterm"
                                            control={<Radio />}
                                            label="Midterm Report"
                                        />
                                        <FormControlLabel
                                            value="final"
                                            control={<Radio />}
                                            label="Final Report"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        )}

                        {/* Step 4: Include Exam Score */}
                        {selectedCourses.length > 0 && (
                            <Box mb={3}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">
                                        <Typography variant="h6" mb={1}>
                                            4. Include {reportType === 'midterm' ? 'Midterm' : 'Final'} Exam Score?
                                        </Typography>
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={includeExamScore ? 'yes' : 'no'}
                                        onChange={(e) => {
                                            const include = e.target.value === 'yes';
                                            setIncludeExamScore(include);
                                            if (!include) {
                                                setExamScores({});
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="no"
                                            control={<Radio />}
                                            label="No"
                                        />
                                        <FormControlLabel
                                            value="yes"
                                            control={<Radio />}
                                            label="Yes"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        )}

                        {/* Step 5: Exam Score Input - Individual for each course */}
                        {selectedCourses.length > 0 && includeExamScore && (
                            <Box mb={3}>
                                <Typography variant="h6" mb={2}>
                                    5. Enter {reportType === 'midterm' ? 'Midterm' : 'Final'} Exam Scores
                                </Typography>
                                {selectedCourses.map((course) => (
                                    <Box key={course.uuid} mb={2}>
                                        <TextField
                                            label={`${course.code || course.course_title} - ${reportType === 'midterm' ? 'Midterm' : 'Final'} Score`}
                                            value={examScores[course.uuid] || ''}
                                            onChange={(e) => setExamScores({
                                                ...examScores,
                                                [course.uuid]: e.target.value
                                            })}
                                            placeholder="e.g., 8/10"
                                            fullWidth
                                            size="small"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Generate Button */}
                        {selectedCourses.length > 0 && (
                            <Box mt={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={handleGenerateReports}
                                    disabled={generating}
                                    fullWidth
                                >
                                    {generating
                                        ? 'Generating...'
                                        : `Generate ${selectedCourses.length} Report${selectedCourses.length > 1 ? 's' : ''}`}
                                </Button>
                                <Typography variant="caption" display="block" mt={1} textAlign="center">
                                    This process may take a few minutes. You'll be redirected to track progress.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default ReportGeneration;
